/**
 * WHO-6344: the page's gateway credential is a 5-minute browser token held in
 * memory only. It is minted by `/api/session/token` from the httpOnly session
 * cookie; nothing a script can read survives the 5 minutes.
 */

/** Refresh this long before the token expires. */
const REFRESH_MARGIN_MS = 60 * 1000;

export type BrowserToken = { accessToken: string; expiresAt: string };

export class SignedOutError extends Error {
  public constructor() {
    super("signed_out");
  }
}

let current: BrowserToken | null = null;
let pending: Promise<BrowserToken | null> | null = null;

const isFresh = (token: BrowserToken | null): token is BrowserToken =>
  !!token &&
  new Date(token.expiresAt).getTime() - Date.now() > REFRESH_MARGIN_MS;

const post = async (url: string, body?: unknown) =>
  await fetch(url, {
    method: "POST",
    credentials: "same-origin",
    headers: body ? { "Content-Type": "application/json" } : undefined,
    body: body ? JSON.stringify(body) : undefined,
  });

const readToken = async (response: Response): Promise<BrowserToken | null> => {
  if (response.status === 401) {
    throw new SignedOutError();
  }

  if (!response.ok) {
    // The gateway or our route is down: no token, but the session stands.
    return null;
  }

  current = (await response.json()) as BrowserToken;
  return current;
};

/** Coalesces concurrent callers onto one request. */
const once = (load: () => Promise<BrowserToken | null>) => {
  if (!pending) {
    pending = load().finally(() => {
      pending = null;
    });
  }

  return pending;
};

/**
 * The current browser token, refreshed when it is about to expire.
 * @throws {SignedOutError} - The session is gone (revoked, expired, logged out elsewhere).
 */
export const fetchBrowserToken = async (
  options: { force?: boolean } = {}
): Promise<BrowserToken | null> => {
  if (!options.force && isFresh(current)) {
    return current;
  }

  return await once(
    async () => await readToken(await post("/api/session/token"))
  );
};

/** After sign-in: swap the login token for an httpOnly session. */
export const establishSession = async (
  signInToken: string
): Promise<BrowserToken | null> => {
  // Never joins a refresh in flight: the login token must reach the route.
  // Refreshes that start meanwhile wait for this exchange instead.
  const exchange = post("/api/session", { token: signInToken }).then(readToken);

  pending = exchange.finally(() => {
    pending = null;
  });

  return await pending;
};

/** Signs this device out at the gateway and clears the session cookies. */
export const endSession = async () => {
  current = null;
  await fetch("/api/session", {
    method: "DELETE",
    credentials: "same-origin",
    keepalive: true,
  }).catch(() => undefined);
};

/** Drops the in-memory token without touching the session (tests, sign-out). */
export const forgetBrowserToken = () => {
  current = null;
};
